import {useEffect, useState} from 'react';

import {Button} from 'shells/button/button';
import {SegmentedControl} from 'shells/card/segmented-control';
import {CreateDecisionFieldChoices} from 'shells/create-custom-field/create-decision-field-choices';
import {ErrorMessage} from 'shells/form/error-message';
import {FormField} from 'shells/form/form-field';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SelectWithIcon} from 'shells/select-picker/select-with-icon/select-with-icon';
import {Switch} from 'shells/switch/switch';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';

import type {FormSchemaProperty} from '../../../../../core/nutshell-fields/types';

import type {CustomFieldView, FieldWithArrayKey} from '../blocks-and-design/content-blocks/types';
import type {SidebarState} from '../sidebar';
import {SidebarPanelHeader} from '../sidebar-panel-header/sidebar-panel-header';

import {
    ENTITY_OPTIONS,
    SOURCE_OPTIONS,
    VIEW_LABELS,
    VIEWS_WITH_CHOICES,
    VIEWS_WITHOUT_PLACEHOLDER,
} from './consts';
import {ExistingFieldPicker} from './existing-field-picker';
import type {NewCustomFieldFormPayload, Source} from './types';

interface Props {
    view: CustomFieldView;
    setSidebarState: (state: SidebarState) => void;
    onPickExistingSchemaField: (field: FormSchemaProperty) => void;
    onSubmitNewField: (payload: NewCustomFieldFormPayload) => Promise<void>;
    formFields: FieldWithArrayKey[];
}

export function AddCustomField(props: Props) {
    const [isRequired, setIsRequired] = useState<boolean>(false);
    const [source, setSource] = useState<Source>('new');
    const [label, setLabel] = useState<string>('');
    const [placeholder, setPlaceholder] = useState<string>('');
    const [entity, setEntity] = useState<string>(ENTITY_OPTIONS[0]?.value ?? '');
    const [choices, setChoices] = useState<string[]>([]);
    const [clientValidationError, setClientValidationError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const title = `Add ${VIEW_LABELS[props.view]}`.toUpperCase();
    const showChoices = VIEWS_WITH_CHOICES.has(props.view);
    const showPlaceholder = !VIEWS_WITHOUT_PLACEHOLDER.has(props.view);

    useEffect(() => {
        setIsRequired(false);
        setSource('new');
        setLabel('');
        setPlaceholder('');
        setEntity(ENTITY_OPTIONS[0]?.value ?? '');
        setChoices([]);
        setClientValidationError(null);
    }, [props.view]);

    const handleSourceChange = (newSource: Source) => {
        setSource(newSource);
    };

    const handleAddToForm = () => {
        setClientValidationError(null);
        const trimmedLabel = label.trim();
        if (!trimmedLabel) {
            setClientValidationError('Enter a label.');
            return;
        }
        if (showChoices) {
            const nonEmptyChoices = choices.map((c) => c.trim()).filter(Boolean);
            if (nonEmptyChoices.length === 0) {
                setClientValidationError('Add at least one choice.');
                return;
            }
        }
        const payload: NewCustomFieldFormPayload = {
            view: props.view,
            label: trimmedLabel,
            placeholder: placeholder.trim(),
            entityName: entity,
            choices: choices.map((c) => c.trim()).filter(Boolean),
            isRequired,
        };
        setIsSubmitting(true);
        void props.onSubmitNewField(payload).finally(() => {
            setIsSubmitting(false);
        });
    };

    if (isSubmitting) {
        return <LoadingPage padding={80} />;
    }

    return (
        <div>
            <SidebarPanelHeader
                title={title}
                onBack={() => props.setSidebarState('blocks-and-design')}
            />
            <div className='pad-16 flex flex-dir-col gap-24'>
                {clientValidationError && <ErrorMessage>{clientValidationError}</ErrorMessage>}
                <Stack isHorizontal={true} justifyContent='space-between' alignItems='center'>
                    <Body>Required</Body>
                    <Switch
                        isOn={isRequired}
                        isRounded={true}
                        isSmall={true}
                        onChange={() => setIsRequired(!isRequired)}
                    />
                </Stack>
                <FormField innerId='source' label='Source' noMargin={true}>
                    <SegmentedControl
                        buttonOptions={SOURCE_OPTIONS}
                        selectedValue={source}
                        setSelectedValue={handleSourceChange}
                    />
                </FormField>
                {source === 'new' && (
                    <>
                        <FormField innerId='label' label='Label' noMargin={true}>
                            <TextField
                                placeholder='Field label'
                                value={label}
                                onChange={setLabel}
                            />
                        </FormField>
                        {showPlaceholder && (
                            <FormField innerId='placeholder' label='Placeholder' noMargin={true}>
                                <TextField
                                    placeholder='Placeholder text'
                                    value={placeholder}
                                    onChange={setPlaceholder}
                                />
                            </FormField>
                        )}
                        <FormField innerId='entity' label='Entity' noMargin={true}>
                            <SelectWithIcon
                                name='entity'
                                options={ENTITY_OPTIONS}
                                value={entity}
                                onChange={setEntity}
                            />
                        </FormField>
                        {showChoices && (
                            <CreateDecisionFieldChoices
                                choices={choices}
                                onUpdateChoices={setChoices}
                            />
                        )}
                    </>
                )}
                {source === 'existing' && (
                    <ExistingFieldPicker
                        view={props.view}
                        fields={props.formFields}
                        onSelect={props.onPickExistingSchemaField}
                    />
                )}
                {source === 'new' && (
                    <Button
                        variant='primary'
                        size='big'
                        isFullWidth={true}
                        disabled={isSubmitting}
                        onClick={handleAddToForm}
                    >
                        Add to form
                    </Button>
                )}
            </div>
        </div>
    );
}
